import type { Setup } from "./settings-url.js";

/**
 * What the settings screen says about where settings live (web ticket 18,
 * story 61).
 *
 * Here rather than in the component for the reason the landing copy is: it is
 * asserted on. This is the one criterion of the ticket with no mechanism behind
 * it — nothing in the code can make a person understand where their font size is
 * kept — and being honest about it is the deliverable. A vlogger who clears
 * their browsing data six months from now and finds their rig gone is a support
 * problem if nobody told them, and is not if somebody did.
 */

/** One line of it: the short form, and the sentence under it. */
export interface StorageNote {
  what: string;
  detail: string;
}

/**
 * Where settings live, what ends them, and the two ways out.
 *
 * In that order, and the last one is why the first two are worth saying at all:
 * a limitation stated with no answer beside it reads as an apology. Both answers
 * are on the same screen as this text, so "export it" and "bookmark it" are
 * pointing at controls the reader can see.
 */
export const SETTINGS_LIVE_HERE: readonly StorageNote[] = [
  {
    what: "These settings live in this browser, on this machine.",
    detail:
      "They are kept in this browser profile's own storage. Another browser, " +
      "another profile, another computer or a private window each start from " +
      "the defaults — and clearing your site data or browsing data for this " +
      "site removes them for good.",
  },
  {
    what: "There is no account, because there is no server to keep one on.",
    detail:
      "cuekards has nothing to sync settings to and nothing to sign in to. That " +
      "is the same reason your audio, your video and your script never leave " +
      "this browser, and it is a deliberate trade rather than a missing " +
      "feature.",
  },
  {
    what: "So keep a copy: export the file, or bookmark the link.",
    detail:
      "The file is everything, including your camera and microphone choices, " +
      "and it is what to import on a second machine. The link carries the " +
      "part of your setup that means something anywhere — the prompt size, " +
      "the scroll speed, voice commands and your keys — so a bookmark rebuilds " +
      "your rig even after site data has been cleared.",
  },
];

/** What the link is for, above the box holding it. */
export const SETUP_IN_THE_LINK =
  "This address carries your setup. Bookmark it, or send it to your other " +
  "machine, and opening it puts the prompter back the way it is now. A script " +
  "you loaded from a Google Doc link comes along with it.";

/** What each thing a URL can carry is called on screen. Beside the copy rather
 * than beside the parameter names, because these are words a person reads and
 * `font=` is not. Typed against {@link Setup}, so a fifth thing a URL can carry
 * cannot arrive without a name for it. */
const SETUP_LABELS = {
  fontSize: "font size",
  wpm: "constant scroll speed",
  voiceCommands: "voice commands",
  shortcuts: "shortcut keys",
} as const satisfies Record<keyof Setup, string>;

/**
 * That this page is running on a setup from its own address, and which parts —
 * or nothing, when it is not.
 *
 * On screen wherever a setup is in force, because the alternative is somebody
 * finding a font size they did not choose, changing it, and finding it back
 * again next time they follow the same bookmark. Both halves of that need
 * saying: which settings the link is providing, and that changing them here does
 * not save them over what this browser has.
 */
export function settingsFromTheLink(setup: Setup): string | undefined {
  const named = (Object.keys(SETUP_LABELS) as (keyof Setup)[])
    .filter((field) => setup[field] !== undefined)
    .map((field) => SETUP_LABELS[field]);
  if (named.length === 0) return undefined;
  return (
    `The link you opened sets the ${list(named)}. Those are in force while you ` +
    `are on this address and are not saved in this browser — import a settings ` +
    `file if you want to keep them here.`
  );
}

/** `a`, `a and b`, `a, b and c`. */
function list(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)!}`;
}
